/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { Session } from "../../entities";
import { PlaybackContentType, SessionState } from "../enums";
import {
  LinearStreamingSessionImpl,
  NdvrStreamingSessionImpl,
  SocuStreamingSessionImpl,
  VodStreamingSessionImpl
} from "../impls";
import type {
  IPlaybackError,
  ISessionCreateModel,
  ISessionManager,
  IStreamingSession
} from "../interfaces";
import type { ISubscription } from "../ISubscription";
import { SubscriptionModel } from "../SubscriptionModel";
import { Log, Diagnostics } from "../../logging";

/**
 * Orchestrates the streaming session lifecycle.
 * Acts as a factory — selects the correct IStreamingSession implementation
 * based on PlaybackContentType, delegates all session operations to it,
 * and broadcasts SessionState changes to subscribers.
 */
export class SessionManager implements ISessionManager {
  private static readonly TAG: string = "SessionManager";

  private mStreamingSession: IStreamingSession;

  private mSubscription: SubscriptionModel<SessionState>;

  private mKeepAliveTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Advanced by every createSession() and sessionStop(). A create response is
   * only acted on while this still holds the value its request captured, so a
   * response that lands after the session was stopped — a zap press stops
   * playback and the next tune only starts once the debounce fires — is
   * dropped instead of being reported for a channel the user has left.
   */
  private mRequestGeneration: number = 0;

  public constructor() {
    this.mSubscription = new SubscriptionModel<SessionState>();
    this.mStreamingSession = new LinearStreamingSessionImpl();
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](SessionManager.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      SessionManager.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(SessionManager.TAG, "stacktrace:,", optionalParams[0]);
    }
  }

  // ── Keep-alive timer ───────────────────────────────────────────────────────

  private startKeepAliveInterval(intervalMs: number): void {
    this.stopKeepAliveTimer();
    this.mStreamingSession.sessionKeepAlive();
    this.mKeepAliveTimerId = setInterval(() => {
      this.mStreamingSession.sessionKeepAlive();
    }, intervalMs) as unknown as ReturnType<typeof setTimeout>;
  }

  private stopKeepAliveTimer(): void {
    if (this.mKeepAliveTimerId !== null) {
      clearTimeout(this.mKeepAliveTimerId);
      clearInterval(
        this.mKeepAliveTimerId as unknown as ReturnType<typeof setInterval>
      );
      this.mKeepAliveTimerId = null;
    }
  }

  // ── ISessionManager ────────────────────────────────────────────────────────

  public createSession(sessionCreateModel: ISessionCreateModel): void {
    const { playbackContentType } = sessionCreateModel;

    this.stopKeepAliveTimer();
    if (this.mStreamingSession.getSessionState() !== SessionState.NONE) {
      this.mStreamingSession.sessionStop();
    }

    if (
      playbackContentType === PlaybackContentType.NDVR_LIVE ||
      playbackContentType === PlaybackContentType.NDVR_RECORDED
    ) {
      this.mStreamingSession = new NdvrStreamingSessionImpl();
    } else if (playbackContentType === PlaybackContentType.VOD) {
      this.mStreamingSession = new VodStreamingSessionImpl();
    } else if (
      playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
      playbackContentType === PlaybackContentType.STARTOVER_RESUME ||
      playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
      playbackContentType === PlaybackContentType.CATCHUP_RESUME
    ) {
      this.mStreamingSession = new SocuStreamingSessionImpl();
    } else {
      // LIVE
      this.mStreamingSession = new LinearStreamingSessionImpl();
    }

    this.log("info", `createSession: contentType=${playbackContentType}`);

    // Captured so a response can be matched to the request that produced it.
    // A tune that lands while this one is in flight replaces
    // mStreamingSession, and a stop advances the generation; either way the
    // late response must then neither schedule keep-alives nor report its
    // state as the current content's.
    const streamingSession: IStreamingSession = this.mStreamingSession;
    this.mRequestGeneration += 1;
    const generation: number = this.mRequestGeneration;

    streamingSession
      .createStreamingSession(sessionCreateModel)
      .then(() => {
        if (!this.isCurrentSession(streamingSession, generation)) {
          // Superseded or stopped while in flight, so whatever ended it saw
          // no backend session yet: its state was still NONE then. Release it
          // here, or the backend session it created is left open with nothing
          // to keep it alive.
          streamingSession.sessionStop();
          return;
        }
        const { firstKeepAliveSeconds, keepAliveIntervalSeconds } =
          this.getSession();
        if (keepAliveIntervalSeconds > 0) {
          const firstDelaySeconds =
            firstKeepAliveSeconds === undefined
              ? keepAliveIntervalSeconds
              : firstKeepAliveSeconds;
          const firstDelayMs = firstDelaySeconds * 1000;
          this.mKeepAliveTimerId = setTimeout(() => {
            this.startKeepAliveInterval(keepAliveIntervalSeconds * 1000);
          }, firstDelayMs);
        }
      })
      .catch((error: unknown) => {
        this.log("error", "createSession failed", error as string);
      })
      .finally(() => {
        if (!this.isCurrentSession(streamingSession, generation)) {
          this.log(
            "info",
            `createSession: dropped stale response for contentType=${playbackContentType}`
          );
          return;
        }
        this.mSubscription.notify(this.getSessionState());
      });
  }

  /**
   * Whether `streamingSession` is still the session this manager is serving,
   * i.e. no later createSession() has replaced it and no sessionStop() has
   * ended it since it was requested.
   */
  private isCurrentSession(
    streamingSession: IStreamingSession,
    generation: number
  ): boolean {
    return (
      streamingSession === this.mStreamingSession &&
      generation === this.mRequestGeneration
    );
  }

  public getSession(): Session {
    return this.mStreamingSession.getSessionInfo();
  }

  public getSessionError(): IPlaybackError {
    return this.mStreamingSession.getSessionPlaybackError();
  }

  public getSessionState(): SessionState {
    return this.mStreamingSession.getSessionState();
  }

  public getSessionStateChangeListener(): ISubscription<SessionState> {
    return this.mSubscription;
  }

  public sessionStop(): void {
    // Keep-alives belong to the session being stopped; left running they
    // would keep firing against a session that no longer exists.
    this.stopKeepAliveTimer();
    this.mRequestGeneration += 1;
    this.mStreamingSession.sessionStop();
  }

  // ── Additional methods used by VideoPlaybackController ─────────────────────

  public setSessionError(
    errorCode: string,
    errorMessage: string,
    errorTitle?: string
  ): void {
    this.mStreamingSession.setSessionPlaybackError(
      this.mStreamingSession.getPlaybackError(
        errorCode,
        errorMessage,
        errorTitle
      )
    );
  }

  public setSessionState(state: SessionState): void {
    this.mStreamingSession.setSessionState(state);
  }
}
